import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Space,
  Input,
  Form,
  Switch,
  Popconfirm,
  Tooltip,
  Tag,
  AutoComplete,
  Alert,
} from "antd";
// `message` estático de antd no funciona en v5 + React 19 — usar bridge.
// Ver utils/antdStatic.ts y feedback_antd_static_bridge.
import { getMsg } from "../../utils/antdStatic";
import { PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { paginacionCliente } from "../../utils/tablaPaginacion";
import {
  listExpenseRecipients,
  createExpenseRecipient,
  updateExpenseRecipient,
  deleteExpenseRecipient,
  type ExpenseRecipient,
} from "../../services/expenseRecipients.api";

const CATEGORY_SUGGESTIONS = [
  { value: "servicios" },
  { value: "insumos" },
  { value: "impuestos" },
  { value: "general" },
];

// Defensivo: el modelo `ExpenseRecipient` tipa `nombre` como string, pero
// la columna del backend no tiene NOT NULL en sanitización del POST, así
// que un row corrupto podría llegar con `nombre = null`. Sin el `?? ""`,
// `null.trim()` reventaría toda la modal del manager.
const isProtected = (r: ExpenseRecipient) =>
  (r.nombre ?? "").trim().toLowerCase() === "otro";

export interface ExpenseRecipientsManagerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Si se entrega, el manager abre directamente en modo "crear nuevo".
   * Útil cuando se invoca desde un botón "+ Crear receptor externo" inline.
   */
  initialMode?: "list" | "create";
  /**
   * Callback que se dispara cuando la lista de receptores externos cambia
   * (crear, editar, eliminar). El padre puede usarlo para refrescar
   * su Select y, opcionalmente, autoseleccionar un receptor externo recién creado.
   */
  onChange?: (recipients: ExpenseRecipient[], lastCreated?: ExpenseRecipient) => void;
}

export function ExpenseRecipientsManager({
  open,
  onClose,
  initialMode = "list",
  onChange,
}: ExpenseRecipientsManagerProps) {
  const [recipients, setRecipients] = useState<ExpenseRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRecipient | null>(null);
  const [form] = Form.useForm<{
    nombre: string;
    categoria?: string | null;
    notas?: string | null;
  }>();
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const data = await listExpenseRecipients(true);
    setLoading(false);
    if (data) setRecipients(data);
    return data;
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]);

  useEffect(() => {
    if (open && initialMode === "create") {
      openCreate();
    }
  }, [open, initialMode]);

  const sorted = useMemo(() => {
    return [...recipients].sort((a, b) => {
      const aOtro = isProtected(a) ? 0 : 1;
      const bOtro = isProtected(b) ? 0 : 1;
      if (aOtro !== bOtro) return aOtro - bOtro;
      // Defensivo: si `nombre` es null/undefined, `null.localeCompare(...)`
      // crashea el render del manager. Mismo patrón que en `sectionArriendos`.
      return (a.nombre ?? "").localeCompare(b.nombre ?? "");
    });
  }, [recipients]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEdit = (r: ExpenseRecipient) => {
    setEditing(r);
    form.setFieldsValue({
      nombre: r.nombre,
      categoria: r.categoria ?? undefined,
      notas: r.notas ?? undefined,
    });
    setEditorOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        nombre: values.nombre.trim(),
        categoria: values.categoria?.trim() || null,
        notas: values.notas?.trim() || null,
      };
      let result: ExpenseRecipient | null;
      if (editing) {
        result = await updateExpenseRecipient(editing.id, payload);
      } else {
        result = await createExpenseRecipient(payload);
      }
      if (result) {
        setEditorOpen(false);
        setEditing(null);
        form.resetFields();
        const updated = await refresh();
        if (updated) onChange?.(updated, editing ? undefined : result);
      }
    } catch {
      // validateFields ya muestra errores; apiFetch ya muestra mensajes de fallo
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (r: ExpenseRecipient, value: boolean) => {
    if (isProtected(r)) {
      getMsg()?.warning(
        "El receptor externo 'Otro' está protegido y no puede desactivarse.",
      );
      return;
    }
    const result = await updateExpenseRecipient(r.id, { activo: value });
    if (result) {
      const updated = await refresh();
      if (updated) onChange?.(updated);
    }
  };

  const handleDelete = async (r: ExpenseRecipient) => {
    if (isProtected(r)) {
      getMsg()?.warning(
        "El receptor externo 'Otro' está protegido y no puede eliminarse.",
      );
      return;
    }
    const res = await deleteExpenseRecipient(r.id);
    if (res) {
      if (res.mode === "soft") {
        getMsg()?.success("Receptor externo desactivado (estaba en uso)");
      } else {
        getMsg()?.success("Receptor externo eliminado");
      }
      const updated = await refresh();
      if (updated) onChange?.(updated);
    }
  };

  const columns: ColumnsType<ExpenseRecipient> = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      render: (v, r) => (
        <Space>
          <b>{v}</b>
          {isProtected(r) && <Tag color="blue">Protegido</Tag>}
        </Space>
      ),
    },
    {
      title: "Categoría",
      dataIndex: "categoria",
      key: "categoria",
      width: 160,
      render: (v?: string | null) =>
        v ? <Tag color="orange">{v.charAt(0).toUpperCase() + v.slice(1)}</Tag> : "—",
    },
    {
      title: "Notas",
      dataIndex: "notas",
      key: "notas",
      ellipsis: true,
      render: (v?: string | null) => v || "—",
    },
    {
      title: "Activo",
      dataIndex: "activo",
      key: "activo",
      width: 100,
      render: (v: boolean, r) => {
        const switchEl = (
          <Switch
            checked={v}
            disabled={isProtected(r)}
            onChange={(checked) => handleToggleActivo(r, checked)}
          />
        );
        return isProtected(r) ? (
          <Tooltip title="Receptor externo protegido — no se puede desactivar">
            {switchEl}
          </Tooltip>
        ) : (
          switchEl
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 200,
      render: (_, r) => {
        const protectedTooltip =
          "Receptor externo protegido — no se puede editar ni eliminar";
        const editBtn = (
          <Button
            size="small"
            disabled={isProtected(r)}
            onClick={() => openEdit(r)}
          >
            Editar
          </Button>
        );
        const deleteBtn = (
          <Popconfirm
            title="¿Eliminar receptor externo?"
            description="Si está en uso, será desactivado (soft delete)."
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            disabled={isProtected(r)}
            onConfirm={() => handleDelete(r)}
          >
            <Button size="small" danger disabled={isProtected(r)}>
              Eliminar
            </Button>
          </Popconfirm>
        );
        if (isProtected(r)) {
          return (
            <Space>
              <Tooltip title={protectedTooltip}>{editBtn}</Tooltip>
              <Tooltip title={protectedTooltip}>{deleteBtn}</Tooltip>
            </Space>
          );
        }
        return (
          <Space>
            {editBtn}
            {deleteBtn}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title="Gestionar receptores externos de gastos"
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Cerrar
          </Button>,
        ]}
        width={820}
        destroyOnClose
      >
        {/*
          El calificativo "externo" no es decorativo: es lo que separa este
          concepto del de "lavandería que recibe el pago". Ver el guard
          `assertNoEsLavanderia` en expenseRecipient.controller.js.
        */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Qué es un receptor externo"
          description={
            <>
              <p style={{ marginBottom: 6 }}>
                Un receptor externo es <b>cualquier otra empresa</b> que recibe
                dinero de Aloja Fácil y no es usuaria del sistema: servicios
                básicos (internet, agua, luz), impuestos, tiendas y proveedores
                de insumos, o servicios profesionales (por ejemplo, un
                contador). El receptor <b>«Otro»</b> viene con el sistema y
                sirve de comodín.
              </p>
              <p style={{ marginBottom: 0 }}>
                <b>Las lavanderías no se registran aquí.</b> Los pagos a
                lavandería —tanto las AF internas como la Lavandería General
                Externa— van en <b>/lavanderia</b>. Si se cargaran como gasto a
                un receptor externo quedarían invisibles para la reconciliación
                de /lavanderia y se contarían dos veces en la utilidad. Por eso
                el sistema rechaza crear un receptor externo cuyo nombre
                coincida con una lavandería del catálogo.
              </p>
            </>
          }
        />
        <Space style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            Agregar receptor externo
          </Button>
          <span style={{ color: "#888" }}>
            {recipients.length}{" "}
            {recipients.length === 1
              ? "receptor externo registrado"
              : "receptores externos registrados"}
          </span>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sorted}
          columns={columns}
          size="small"
          pagination={paginacionCliente({ porDefecto: 10, etiqueta: "receptor" })}
        />
      </Modal>

      <Modal
        title={editing ? "Editar receptor externo" : "Nuevo receptor externo"}
        open={editorOpen}
        onCancel={() => {
          setEditorOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={submit}
        confirmLoading={saving}
        okText={editing ? "Guardar" : "Crear"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[
              { required: true, message: "Ingresa un nombre" },
              { max: 120, message: "Máximo 120 caracteres" },
            ]}
          >
            <Input placeholder="Ej: Movistar, Aguas Andinas, SII" autoFocus />
          </Form.Item>

          <Form.Item name="categoria" label="Categoría (opcional)">
            <AutoComplete
              options={CATEGORY_SUGGESTIONS}
              placeholder="Ej: servicios, insumos, impuestos, general"
              allowClear
              filterOption={(input, option) =>
                (option?.value ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="notas" label="Notas (opcional)">
            <Input.TextArea
              rows={3}
              placeholder="Detalles internos sobre este receptor externo"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default ExpenseRecipientsManager;
